// HQ curates the global game catalogue (F-CAT-01, F-CAT-07; spec/14 §6, spec/04 §5, spec/07 §7).
//
//   GET  /v1/hq/catalogue/search?q=   search IGDB for base games to mirror                 hq:catalog
//   POST /v1/hq/catalogue/games       mirror a base game into the catalogue by its igdbId  hq:catalog
//   GET  /v1/hq/catalogue/games       every game mirrored so far, newest first             hq:catalog
//   GET  /v1/hq/catalogue/games/{id}/art-candidates?kind=    hero/logo candidates        hq:catalog
//   PUT  /v1/hq/catalogue/games/{id}/art/{kind}              pick one → copied to R2     hq:catalog
//   DELETE /v1/hq/catalogue/games/{id}/art/{kind}            clear it                    hq:catalog
//   PUT  /v1/hq/catalogue/games/{id}/detection               detection rules (any title) hq:catalog
//   POST /v1/hq/catalogue/apps                               add an app (not a game)     hq:catalog
//   PATCH /v1/hq/catalogue/apps/{id}                         edit an app (incl. its provisioning recipe and sign-out paths, F-CAT-09) hq:catalog
//   POST /v1/hq/catalogue/apps/common                        add the common apps         hq:catalog
//   POST /v1/hq/catalogue/apps/{id}/icon                     signed icon upload          hq:catalog
//   POST /v1/hq/catalogue/apps/{id}/icon/{uploadId}/complete confirm it → copied to R2   hq:catalog
//   POST /v1/hq/catalogue/games/manual                       create a game by hand (M3)  hq:catalog
//   PATCH /v1/hq/catalogue/games/{id}                        edit a game (kept through refreshes) hq:catalog
//   GET  /v1/hq/catalogue/games/{id}/usage                   venues offering it          hq:catalog
//   POST /v1/hq/catalogue/games/{id}/archive · /unarchive    archive, never delete       hq:catalog
//   POST /v1/hq/catalogue/detection/test                     test a pasted process list  hq:catalog
//   (title requests — GET /v1/hq/catalogue/requests, POST …/{requestId}/resolve — live in station-installs.ts, F-CAT-08)
//
// **Apps** (spec/14 §6): the catalogue also holds non-game titles HQ adds by hand — no IGDB record, a
// square icon instead of a cover, a suggested kiosk category — which venues add to their library and
// players open from the kiosk's Apps screen exactly as they launch a game.
//
// **The catalogue is curated, not bulk-imported** (the product owner, 2026-09-22). HQ searches the
// metadata provider (IGDB) — filtered to **base games only** (`category = main_game` and no
// `version_parent`, with a cover, most popular first), never DLC, editions or bundles — picks one,
// and the server **mirrors** it into our own `game` table: our own `gam_…` id, the source `igdbId`
// beside it, and the metadata we keep (name, summary, cover reference, genres, franchise, first
// release year). Picking a non-base title is resolved up to its base-game root before it is saved,
// and the catalogue **dedupes on `igdbId`** — mirroring the same game twice returns the row already
// there rather than a second one.
//
// **IGDB is never called from a client.** It is rate-limited behind Twitch OAuth, so it is a
// server-side proxy with caching (`apps/server/src/modules/catalog`); these HQ endpoints are the
// only way in, and they are gated by `hq:catalog` and a second factor. Search and the mirror need
// the provider configured (`TWITCH_CLIENT_ID`/`TWITCH_CLIENT_SECRET`); without it they answer
// `503 catalogue-provider-unavailable`. Listing what is already mirrored reads our own table and
// needs no provider.
//
// **Cover art** is cached to our own object storage on mirror (spec/14 §6): the server fetches
// IGDB's `t_cover_big` JPG and stores it in the public-media bucket, and `coverUrl` is our copy on
// the media CDN (`PUBLIC_MEDIA_URL`). `coverImageId` stays the IGDB reference and the fallback —
// `coverUrl` is `null` when we hold no copy (object storage unconfigured, or not yet cached), and a
// client then resolves `coverImageId` to IGDB's image CDN. `POST …/{id}/refresh` re-fetches the base
// game and re-uploads the cover (F-CAT-07). Search results still carry only the IGDB reference.
//
// Not here, and deferred (see the pull request): the venue library (which catalogue games a venue
// installs, F-CAT-02), install discovery (F-CAT-03), station game detection (F-CAT-04), trailer
// uploads and the fuller `title` with launch recipes and detection rules (spec/14 §6). `POST
// /catalogue/games` is also the **one-click accept** seam an auto-detected-but-unmatched game will
// use once detection feeds it (F-CAT-04): HQ confirms the IGDB match and it is mirrored the same way.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { GameIdSchema, GameTrailerIdSchema } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_CATALOG } from "../permissions/index.js";
import { httpsHostOf, INSTALL_ROOT_PATTERN, isBroadInstallRoot, isInsideInstallRoot, isProtectedProcessName, PER_USER_PATH_PATTERN, PROVISIONING_CHILD_TOOLS, RESTORE_EXCLUDE_PATTERN, } from "../primitives/app-provisioning.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { DETECTION_TEST_RULES, DETECTION_TEST_VIAS, MAX_DETECTION_TEST_LINES } from "../primitives/title-detection-match.js";
import { PERMISSION_EXTENSION } from "./audit.js";
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
/**
 * An IGDB id: the provider's own numeric id for a game, which the catalogue keeps beside our id so a
 * refresh knows which record to re-read (spec/14 §6 "metadata source id"). A positive integer.
 */
export const IgdbIdSchema = z.int().positive().openapi('IgdbId', {
    description: 'IGDB’s own numeric id for the game — the catalogue’s `metadataSource` (spec/14 §6).',
    example: 1020,
});
/**
 * The cover reference IGDB gives an image — its `image_id`, e.g. `co1rbo`. A client resolves it to a
 * URL; caching the image to our object storage is a flagged follow-up (spec/14 §6, the R2 art seam).
 */
export const CoverImageIdSchema = z.string().min(1).max(128).openapi('CoverImageId', {
    description: 'IGDB’s cover `image_id`; a client builds the art URL from it. Caching to our storage is a follow-up.',
    example: 'co1rbo',
});
// ---------------------------------------------------------------------------------------------
// Launch recipes — how a station launches a catalogue game (F-CAT-05; spec/10 §5.2, spec/14 §6)
// ---------------------------------------------------------------------------------------------
//
// A launch recipe is **platform-level metadata HQ curates on a game** ("launch recipes per launcher",
// spec/14 §6): the catalogue suggests one per launcher, and the venue library carries the game's
// recipes to the station in its signed bundle (spec/10 §7.1). The station launches by the recipe's
// `type` (spec/10 §5.2):
//
//   exe    `CreateProcess` as the user, with arguments and a working directory
//   uri    a launcher URI via `ShellExecuteEx` (`steam://rungameid/…`, `com.epicgames.launcher://…`, …)
//   store  `IApplicationActivationManager.ActivateApplication` with the full Store AUMID
//   lnk    a `.lnk`/`.url` shortcut the agent resolves, then launches as above
//
// Path variables (`{ProgramFiles}`, `{SteamLibrary}`, `{EpicInstall:<AppName>}`, `{UserProfile}`) are
// **resolved on the PC** (spec/10 §5.2), so the server stores the templated strings verbatim; it does
// not resolve or validate them against any filesystem. The **venue** override (`launcherChoice`,
// `customLaunch` on a library entry) is the deferred follow-on that lands with the fuller `title`
// (spec/04 §5); this is the global recipe every venue draws from.
/**
 * The launcher a recipe drives (spec/10 §5.2, spec/14 §6). `standalone` is a game with no launcher (a
 * direct exe or shortcut); `other` is any launcher not named here. HQ suggests one recipe per launcher.
 */
export const LAUNCH_RECIPE_LAUNCHERS = ['steam', 'epic', 'ea', 'ubisoft', 'battlenet', 'riot', 'microsoft_store', 'standalone', 'other'];
export const LaunchRecipeLauncherSchema = z.enum(LAUNCH_RECIPE_LAUNCHERS).openapi('LaunchRecipeLauncher', {
    description: 'The launcher a launch recipe drives (spec/10 §5.2): Steam, Epic, EA, Ubisoft, Battle.net, Riot, the Microsoft Store, a standalone game, or another launcher.',
});
/** The launch types a station knows (F-CAT-05, spec/10 §5.2): `exe`, `uri`, `store`, `lnk`. */
export const LAUNCH_RECIPE_TYPES = ['exe', 'uri', 'store', 'lnk'];
export const LaunchRecipeTypeSchema = z.enum(LAUNCH_RECIPE_TYPES).openapi('LaunchRecipeType', {
    description: 'How a station launches the game (spec/10 §5.2): `exe` (a process with args and a working directory), `uri` (a ' +
        'launcher URI via ShellExecuteEx), `store` (a Microsoft Store AUMID), or `lnk` (a shortcut the agent resolves).',
});
/** A path or shortcut on the PC, with path variables the agent resolves (spec/10 §5.2). Stored verbatim. */
const launchPathSchema = z
    .string()
    .trim()
    .min(1)
    .max(1024)
    .openapi({ description: 'A path on the station. Path variables (`{ProgramFiles}`, `{SteamLibrary}`, …) are resolved on the PC.', example: '{SteamLibrary}\\steamapps\\common\\Game\\game.exe' });
/**
 * One way a station launches a catalogue game (F-CAT-05, spec/10 §5.2). The `type` picks the mechanism
 * and which of the optional fields it uses:
 *
 *   exe    `path` (required), `args`, `workingDirectory`
 *   uri    `uri` (required) — e.g. `steam://rungameid/730`
 *   store  `aumid` (required) — the full Store AUMID
 *   lnk    `path` (required) — a `.lnk`/`.url` shortcut the agent resolves
 *
 * A flat object rather than a discriminated union so the generated TypeScript client and the C# station
 * DTOs both carry it (the codegen emits objects, not `oneOf`); the per-`type` rules are enforced by the
 * schema's refinement, so an `exe` with no `path`, or a `uri` carrying an `aumid`, is rejected.
 */
export const LaunchRecipeSchema = z
    .object({
    /** The launcher this recipe drives. HQ suggests one recipe per launcher. */
    launcher: LaunchRecipeLauncherSchema,
    /** Which launch mechanism this recipe uses; it decides which fields below apply. */
    type: LaunchRecipeTypeSchema,
    /** HQ's label for the recipe, e.g. "Steam". Optional. */
    label: z.string().trim().min(1).max(120).optional().openapi({ description: 'HQ’s label for the recipe.', example: 'Steam' }),
    /** The executable (`exe`) or shortcut (`lnk`) path. Required for `exe` and `lnk`; unused otherwise. */
    path: launchPathSchema.optional(),
    /** Command-line arguments for an `exe` launch. Optional; unused for other types. */
    args: z.string().max(2048).optional().openapi({ description: 'Command-line arguments for an `exe` launch.' }),
    /** The working directory for an `exe` launch. Optional; unused for other types. */
    workingDirectory: launchPathSchema.optional(),
    /** The launcher URI for a `uri` launch. Required for `uri`; unused otherwise. */
    uri: z.string().trim().min(1).max(2048).optional().openapi({ description: 'A launcher URI, e.g. `steam://rungameid/730`.', example: 'steam://rungameid/730' }),
    /** The full Store AUMID for a `store` launch. Required for `store`; unused otherwise. */
    aumid: z.string().trim().min(1).max(512).optional().openapi({ description: 'The full Store AUMID.', example: 'Microsoft.MinecraftUWP_8wekyb3d8bbwe!App' }),
})
    .strict()
    .superRefine((recipe, ctx) => {
    const require = (field) => {
        if (recipe[field] === undefined)
            ctx.addIssue({ code: 'custom', path: [field], message: `A "${recipe.type}" launch recipe needs a ${field}.` });
    };
    const forbid = (fields) => {
        for (const field of fields) {
            if (recipe[field] !== undefined)
                ctx.addIssue({ code: 'custom', path: [field], message: `A "${recipe.type}" launch recipe has no ${field}.` });
        }
    };
    switch (recipe.type) {
        case 'exe':
            require('path');
            forbid(['uri', 'aumid']);
            break;
        case 'lnk':
            require('path');
            forbid(['args', 'workingDirectory', 'uri', 'aumid']);
            break;
        case 'uri':
            require('uri');
            forbid(['path', 'args', 'workingDirectory', 'aumid']);
            break;
        case 'store':
            require('aumid');
            forbid(['path', 'args', 'workingDirectory', 'uri']);
            break;
    }
})
    .openapi('LaunchRecipe', { description: 'How a station launches a catalogue game — by exe, launcher URI, Store AUMID or shortcut (F-CAT-05, spec/10 §5.2).' });
/** At most this many recipes on a game — one or two per launcher is the shape (spec/14 §6). */
export const MAX_LAUNCH_RECIPES = 16;
// ---------------------------------------------------------------------------------------------
// Game media — hero art and logos (spec/14 §6, ADR-0028; F-CAT-01, F-CAT-07)
// ---------------------------------------------------------------------------------------------
//
// **One flow for all game media** (the product owner, 2026-09-29): HQ picks (or uploads) → the server
// copies it into **our** public-media bucket under a content-addressed, immutable key → stations
// download and cache it → the kiosk plays it locally. A kiosk never hotlinks IGDB or SteamGridDB.
// SteamGridDB is the preferred source for wide heroes and transparent logos; IGDB's artworks and
// screenshots are the fallback hero source (IGDB has no logos).
/**
 * The kinds of still art a title carries beside its cover: a wide `hero`, a transparent `logo`, and a square
 * `icon` — an app's tile on the kiosk Apps screen (spec/14 §6), picked from SteamGridDB or uploaded by HQ.
 */
export const ART_KINDS = ['hero', 'logo', 'icon'];
export const ArtKindSchema = z.enum(ART_KINDS).openapi('ArtKind', {
    description: 'Which piece of title art: `hero` (the wide banner, ≈ 1920 × 620), `logo` (a transparent PNG) or `icon` (a square ' +
        'PNG/WebP — an app’s kiosk tile).',
});
/** Where a piece of game art came from. */
export const ART_SOURCES = ['steamgriddb', 'igdb'];
export const ArtSourceSchema = z.enum(ART_SOURCES).openapi('ArtSource', {
    description: 'The art source a hero or logo was picked from: `steamgriddb` (heroes and logos) or `igdb` (artworks and screenshots, heroes only).',
});
// ---------------------------------------------------------------------------------------------
// Title kinds and detection rules (spec/14 §6, spec/10 §7.1, §7.3; F-CAT-01, F-CAT-04)
// ---------------------------------------------------------------------------------------------
//
// The catalogue holds two kinds of title: a **game** — HQ's mirror of an IGDB base game — and an **app**
// (Chrome, Discord, Spotify, a launcher…) that HQ creates by hand: it has no IGDB record, so its `igdbId` is
// `null`, and it carries a square **icon** instead of a cover. Both are rows of the same catalogue, both
// ride the venue library and the station's signed bundle identically, and both launch through the same
// launch recipes. An app also names the kiosk category a venue most likely wants it under
// (`suggestedCategory`), which the venue library defaults to.
/** The kinds of title the catalogue holds (spec/14 §6): an IGDB-mirrored `game`, or an HQ-created `app`. */
export const CATALOGUE_KINDS = ['game', 'app'];
export const CatalogueKindSchema = z.enum(CATALOGUE_KINDS).openapi('CatalogueKind', {
    description: 'The kind of catalogue title: `game` (mirrored from IGDB) or `app` (a non-game application HQ added — a browser, voice chat, a launcher…).',
});
/**
 * The kiosk categories an app may suggest (spec/13 §9.1) — the non-game subset of the library's
 * categories (`LIBRARY_CATEGORIES` in `library.ts`, which builds on this file, so the codes are repeated
 * here rather than imported). A venue may still file an app under any category.
 */
export const APP_CATEGORIES = ['apps', 'browsers', 'voice', 'media', 'tools'];
export const AppCategorySchema = z.enum(APP_CATEGORIES).openapi('AppCategory', {
    description: 'The kiosk category an app suggests a venue files it under: `apps`, `browsers`, `voice`, `media` or `tools`.',
});
/** At most this many process names on one title. */
export const MAX_DETECTION_PROCESS_NAMES = 32;
/**
 * A process image name a title runs as — `chrome`, `Discord`, `obs64` — matched case-insensitively with
 * `.exe` stripped on both sides (spec/10 §7.3). The server stores it normalised: lower case, no `.exe`.
 * A bare file name, never a path.
 */
const processNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[^\\/:*?"<>|]+$/, 'A process name is a file name, not a path.')
    .openapi({ description: 'A process image name, e.g. `chrome` or `Discord.exe` — stored lower-case without `.exe`.', example: 'chrome' });
/** A detection regex must compile; the station applies it to the process image path (spec/10 §7.3). */
function compiles(pattern) {
    try {
        new RegExp(pattern, 'i');
        return true;
    }
    catch {
        return false;
    }
}
const pathPatternSchema = z
    .string()
    .trim()
    .min(1)
    .max(1024)
    .openapi({ description: 'A wildcard (`*`, `?`) over the process image path. Path variables are resolved on the PC.', example: '{ProgramFiles}\\Google\\Chrome\\*' });
const detectionRegexSchema = z
    .string()
    .min(1)
    .max(512)
    .refine(compiles, 'The regex does not compile.')
    .openapi({ description: 'A case-insensitive regular expression over the process image path.' });
/**
 * How a station recognises a title's process (spec/10 §7.1, §7.3; F-CAT-04): its **process names**
 * (normalised without `.exe`), optionally narrowed by a **path pattern** (a wildcard over the image path,
 * path variables allowed, e.g. `{ProgramFiles}\Google\Chrome\*`), or matched by a **regex** over the
 * image path. Empty `processNames` and no regex means the title is only known when it is launched from its
 * library entry. Carried to the station in the signed library bundle (`LibraryBundleEntry.detection`).
 */
export const TitleDetectionSchema = z
    .object({
    /** The process names, lower case, no `.exe`. Empty when the title has none yet. */
    processNames: z.array(processNameSchema).max(MAX_DETECTION_PROCESS_NAMES),
    /** A wildcard over the process image path that narrows a name match, or `null`. Path variables allowed. */
    pathPattern: pathPatternSchema.nullable(),
    /** A case-insensitive regex over the process image path that matches on its own, or `null`. */
    regex: detectionRegexSchema.nullable(),
})
    .strict()
    .openapi('TitleDetection', {
    description: 'How a station recognises a title’s process (spec/10 §7.3): process names (case-insensitive, `.exe` stripped), an ' +
        'optional path pattern narrowing them, and an optional regex over the image path.',
});
/** What a title detects as until HQ says otherwise: nothing but a launch from its library entry. */
export const EMPTY_TITLE_DETECTION = { processNames: [], pathPattern: null, regex: null };
/** The detection rules as HQ sends them: `pathPattern` and `regex` may be left out (stored as `null`). */
export const TitleDetectionInputSchema = z
    .object({
    processNames: z.array(processNameSchema).max(MAX_DETECTION_PROCESS_NAMES),
    pathPattern: pathPatternSchema.nullable().optional(),
    regex: detectionRegexSchema.nullable().optional(),
})
    .strict()
    .openapi('TitleDetectionInput', { description: 'Detection rules for a catalogue title, as HQ sends them (spec/10 §7.3).' });
/**
 * A process name as it is stored and compared: trimmed, lower case, one trailing `.exe` removed
 * (spec/10 §7.3, "`.exe` stripped on both sides").
 */
export function normaliseProcessName(name) {
    const lower = name.trim().toLowerCase();
    return lower.endsWith('.exe') ? lower.slice(0, -4) : lower;
}
/** HQ's detection input as stored: names normalised and deduplicated in order, absent fields `null`. */
export function normaliseTitleDetection(input) {
    const names = [];
    for (const name of input.processNames) {
        const normalised = normaliseProcessName(name);
        if (normalised !== '' && !names.includes(normalised))
            names.push(normalised);
    }
    return { processNames: names, pathPattern: input.pathPattern ?? null, regex: input.regex ?? null };
}
// ---------------------------------------------------------------------------------------------
// Apps installed for players — per-user apps (F-CAT-09; ADR-0045, spec/10 §7.5, spec/14 §6)
// ---------------------------------------------------------------------------------------------
//
// Some apps install **per Windows account**, not per PC: Discord and Spotify's own installers write to the account that
// ran them (`%LocalAppData%`, `%AppData%`), and a Microsoft Store app (WhatsApp) is registered only for the account that
// installed it. A station's player account never ran those installers, so the app is "not installed" there however the
// venue set up the PC. An HQ app may carry a **provisioning recipe** — how a station installs it into the player account
// itself — and a venue turns **Install for players** on per app (`LibraryEntry.installForPlayers`, default off). Only HQ
// writes a recipe: a venue can never point its PCs at a URL or an executable of its own. Two kinds:
//
//   store      `packageFamilyName` — a Store package already on the PC (installed for any account) is registered for
//              the player; one that is not on the PC is reported `not_on_this_pc` (nothing is downloaded from the Store).
//   installer  the vendor's own per-user installer: an HTTPS `downloadUrl` whose host — and every redirect's host — is in
//              `allowedHosts`; the Authenticode `signer` the file must be signed by (a valid, unrevoked chain whose leaf
//              certificate's subject CN is present and exactly this); `silentArgs`; the per-user `installedPath` that
//              proves it installed; the processes to close if the installer starts the app (`closeProcesses`, never one
//              of the PC's own — `PROTECTED_PROCESS_NAMES`); the `HKCU\…\Run` values to remove after it
//              (`removeRunValues`), so it never starts by itself; and the app's `installRoot` — the folder the station
//              snapshots once it is installed and restores at every session end, so a player cannot leave tampered code
//              for the next one (ADR-0045 §8) — with the data inside it never captured or restored (`restoreExclude`).
//              `firstRunSeconds` lets an app the installer started finish its first-run downloads before the snapshot.
//              `expectedBlockedChildren` names the always-blocked Windows tools (`PROVISIONING_CHILD_TOOLS`: `reg`) the
//              installed app itself starts in a session (Discord, at every start): they
//              stay blocked, but a block whose parent is the verified app (inside `installRoot`, signed by `signer`) is
//              recorded quietly instead of raising a tamper alert.
//
// An app may also carry the **sign-in data** a session end clears so the next player is signed out (`signOutPaths`,
// relative to the player's `%LocalAppData%` or `%AppData%`), carried to the station in the signed library bundle and
// cleared beside the station's own list of launcher sign-ins (spec/11 §5).
/** The kinds of provisioning recipe (ADR-0045): a Store package registered for the player, or the vendor's installer run as the player. */
export const APP_PROVISIONING_KINDS = ['store', 'installer'];
export const AppProvisioningKindSchema = z.enum(APP_PROVISIONING_KINDS).openapi('AppProvisioningKind', {
    description: 'How a station installs an app into the player account (ADR-0045): `store` registers a Microsoft Store package already on the ' +
        'PC for the player; `installer` runs the vendor’s own per-user installer as the player.',
});
/** At most this many hosts on an installer's allow-list, processes to close, or Run values to remove. */
export const MAX_PROVISIONING_HOSTS = 8;
export const MAX_PROVISIONING_CLOSE_PROCESSES = 8;
export const MAX_PROVISIONING_RUN_VALUES = 8;
/** The longest a station waits for an installer, and the shortest it may be told to (seconds). */
export const MIN_PROVISIONING_TIMEOUT_SECONDS = 30;
export const MAX_PROVISIONING_TIMEOUT_SECONDS = 1800;
/** The longest an app the installer started may keep running for its first-run downloads before it is closed (seconds). */
export const MAX_PROVISIONING_FIRST_RUN_SECONDS = 600;
/** At most this many paths inside an app's install root that are data, never snapshotted or restored. */
export const MAX_RESTORE_EXCLUDES = 32;
/**
 * A Microsoft Store package family name: the package name, `_`, and the 13-character publisher id — e.g.
 * `5319275A.WhatsAppDesktop_cv1g1gvanyjgm`.
 */
const packageFamilyNameSchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9][A-Za-z0-9.-]{1,49}_[a-z0-9]{13}$/, 'A package family name is <name>_<13-character publisher id>.')
    .openapi({ description: 'A Microsoft Store package family name.', example: '5319275A.WhatsAppDesktop_cv1g1gvanyjgm' });
/** A host name an installer may be downloaded from: lower case, dotted, no scheme, port or path. */
const provisioningHostSchema = z
    .string()
    .trim()
    .max(253)
    .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'A host name, lower case, with no scheme, port or path.')
    .openapi({ description: 'A host name the installer, and every redirect on the way to it, may come from.', example: 'discord.com' });
/**
 * A per-user path on the PC, with a path variable for the player's own folders — `{LocalAppData}`, `{AppData}` or
 * `{UserProfile}` — first: what proves an installer put the app in the player account. Resolved on the PC (spec/10 §5.2),
 * and for the "installed for another Windows account" check, in each other account's profile.
 */
const PER_USER_PATH = PER_USER_PATH_PATTERN;
const perUserPathSchema = z
    .string()
    .trim()
    .max(512)
    .regex(PER_USER_PATH, 'A path under {LocalAppData}, {AppData} or {UserProfile}, with backslashes and no "..".')
    .openapi({ description: 'A file under the player’s own folders that proves the app is installed for them.', example: '{LocalAppData}\\Discord\\Update.exe' });
/**
 * An app's install root (ADR-0045 §8): its own folder under `{LocalAppData}` or `{AppData}` — at least one folder below the
 * variable, backslashes, no `.`/`..` segment, no environment variables — which the station snapshots once installed and
 * restores at every session end.
 */
const INSTALL_ROOT = INSTALL_ROOT_PATTERN;
const installRootSchema = z
    .string()
    .trim()
    .max(260)
    .regex(INSTALL_ROOT, 'A folder under {LocalAppData} or {AppData}, with backslashes and no "..".')
    .openapi({ description: 'The app’s own install folder, snapshotted once installed and restored at every session end.', example: '{LocalAppData}\\Discord' });
/** A path relative to the install root that holds data, not code (a settings file, a per-account folder): never restored. */
const RESTORE_EXCLUDE = RESTORE_EXCLUDE_PATTERN;
const restoreExcludeSchema = z
    .string()
    .trim()
    .min(1)
    .max(260)
    .regex(RESTORE_EXCLUDE, 'A path relative to the install root: backslashes, no drive, no leading slash, no "..", no variables.')
    .openapi({ description: 'A file or folder inside the install root that is data: never snapshotted or restored.', example: 'prefs' });
/** An always-blocked Windows tool an installed app is known to start (F-CAT-09, ADR-0045 §9): still blocked, not an alert. */
export const ProvisioningChildToolSchema = z.enum(PROVISIONING_CHILD_TOOLS).openapi('ProvisioningChildTool', {
    description: 'An always-blocked Windows tool (spec/11 §3.4) an installed app is known to start (`expectedBlockedChildren`): still ' +
        'blocked, but recorded quietly when the parent is the verified app. `reg`: the Windows registry tool, which Discord runs ' +
        'at every start to register its link handler.',
});
/** A value name under `HKCU\Software\Microsoft\Windows\CurrentVersion\Run` — a name, never a path. */
const runValueNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[^\\]+$/, 'A Run value name, not a path.')
    .openapi({ description: 'A value under HKCU\\…\\CurrentVersion\\Run the app adds to start itself.', example: 'Discord' });
/**
 * How a station installs an app into the player account (F-CAT-09, ADR-0045). A flat object rather than a union, as
 * `LaunchRecipe` is, so the TypeScript client and the C# station DTOs both carry it; the per-`kind` rules are the
 * refinement's:
 *
 *   store      `packageFamilyName` (required); nothing else
 *   installer  `downloadUrl`, `allowedHosts`, `signer`, `installedPath` (required); `silentArgs`, `closeProcesses`,
 *              `removeRunValues`, `timeoutSeconds` (optional); no `packageFamilyName`
 *
 * The download URL must be `https:` and its host in `allowedHosts` (the station also holds every redirect to them).
 */
const AppProvisioningObjectSchema = z
    .object({
    kind: AppProvisioningKindSchema,
    /** `store`: the package's family name — registered for the player when the package is on the PC. */
    packageFamilyName: packageFamilyNameSchema.optional(),
    /** `installer`: the vendor's own "latest" download link, `https:`. */
    downloadUrl: z.string().trim().max(2048).optional().openapi({ description: 'The vendor’s own HTTPS download link for the installer.' }),
    /** `installer`: the hosts the download, and every redirect on the way, may come from. */
    allowedHosts: z.array(provisioningHostSchema).min(1).max(MAX_PROVISIONING_HOSTS).optional(),
    /**
     * `installer`: who must have signed the file — the Authenticode leaf certificate's subject common name, compared
     * exactly (e.g. `Discord Inc.`). The chain must also be valid and unrevoked; an unsigned or other file is refused.
     */
    signer: z.string().trim().min(1).max(200).optional().openapi({ description: 'The Authenticode signer’s name (the leaf certificate’s subject CN), exactly.', example: 'Discord Inc.' }),
    /** `installer`: the arguments that make it install silently, for the player only (e.g. `-s`). */
    silentArgs: z.string().trim().max(512).optional().openapi({ description: 'The installer’s silent-install arguments.', example: '-s' }),
    /** `installer`: the per-user file that proves it installed (`{LocalAppData}\Discord\Update.exe`). */
    installedPath: perUserPathSchema.optional(),
    /**
     * `installer`: process names the installer may start, closed once it has finished (lower case, no `.exe`) — never one of
     * `PROTECTED_PROCESS_NAMES`.
     */
    closeProcesses: z.array(processNameSchema).max(MAX_PROVISIONING_CLOSE_PROCESSES).optional(),
    /** `installer`: the `HKCU\…\Run` values to remove after installing, so the app never starts by itself. */
    removeRunValues: z.array(runValueNameSchema).max(MAX_PROVISIONING_RUN_VALUES).optional(),
    /** `installer`: how long the installer may run before it is stopped and reported `timed_out` (seconds; default 600). */
    timeoutSeconds: z.int().min(MIN_PROVISIONING_TIMEOUT_SECONDS).max(MAX_PROVISIONING_TIMEOUT_SECONDS).optional(),
    /**
     * `installer`: the app's own install folder (`{LocalAppData}\Discord`), which holds `installedPath`. The station snapshots
     * it once the app is installed and, at every session end, checks it file by file and restores it, so code a player
     * changed never reaches the next player (ADR-0045 §8).
     */
    installRoot: installRootSchema.optional(),
    /** `installer`: paths inside `installRoot` that are data (settings, per-account folders): never snapshotted or restored. */
    restoreExclude: z.array(restoreExcludeSchema).max(MAX_RESTORE_EXCLUDES).optional(),
    /**
     * `installer`: how long an app the installer started may keep running to finish its first-run downloads before it is
     * closed and snapshotted — only while the PC stays locked (seconds; default 0: closed as soon as it is seen).
     */
    firstRunSeconds: z.int().min(0).max(MAX_PROVISIONING_FIRST_RUN_SECONDS).optional(),
    /**
     * `installer`: the always-blocked Windows tools (spec/11 §3.4, `PROVISIONING_CHILD_TOOLS` only) the installed app itself
     * starts when a player uses it — Discord.exe runs `reg` at every start. They stay **blocked** in a session; a block whose
     * parent is this app — its image inside `installRoot` and Authenticode-signed by `signer`, never trusted by name — is
     * recorded quietly on the PC as expected instead of raising `tamper_attempt`. Absent: none (every block is an alert).
     */
    expectedBlockedChildren: z.array(ProvisioningChildToolSchema).max(PROVISIONING_CHILD_TOOLS.length).optional(),
})
    .strict();
const STORE_FIELDS = ['packageFamilyName'];
const INSTALLER_FIELDS = [
    'downloadUrl',
    'allowedHosts',
    'signer',
    'silentArgs',
    'installedPath',
    'closeProcesses',
    'removeRunValues',
    'timeoutSeconds',
    'installRoot',
    'restoreExclude',
    'firstRunSeconds',
    'expectedBlockedChildren',
];
const INSTALLER_REQUIRED = ['downloadUrl', 'allowedHosts', 'signer', 'installedPath', 'installRoot'];
/** The per-`kind` rules of a provisioning recipe (shared by the catalogue's schema and the bundle's). */
function checkProvisioning(recipe, ctx) {
    const require = (fields) => {
        for (const field of fields) {
            if (recipe[field] === undefined)
                ctx.addIssue({ code: 'custom', path: [field], message: `A "${recipe.kind}" recipe needs ${field}.` });
        }
    };
    const forbid = (fields) => {
        for (const field of fields) {
            if (recipe[field] !== undefined)
                ctx.addIssue({ code: 'custom', path: [field], message: `A "${recipe.kind}" recipe has no ${field}.` });
        }
    };
    if (recipe.kind === 'store') {
        require(STORE_FIELDS);
        forbid(INSTALLER_FIELDS);
        return;
    }
    require(INSTALLER_REQUIRED);
    forbid(STORE_FIELDS);
    if (recipe.downloadUrl !== undefined) {
        const host = httpsHostOf(recipe.downloadUrl);
        if (host === undefined)
            ctx.addIssue({ code: 'custom', path: ['downloadUrl'], message: 'The download link must be an https: URL.' });
        else if (recipe.allowedHosts !== undefined && !recipe.allowedHosts.includes(host)) {
            ctx.addIssue({ code: 'custom', path: ['allowedHosts'], message: `The download link's host (${host}) is not in allowedHosts.` });
        }
    }
    for (const name of recipe.closeProcesses ?? []) {
        if (isProtectedProcessName(name)) {
            ctx.addIssue({ code: 'custom', path: ['closeProcesses'], message: `"${name}" is one of the PC's own processes and is never closed.` });
        }
    }
    if (recipe.installRoot !== undefined) {
        if (isBroadInstallRoot(recipe.installRoot)) {
            ctx.addIssue({ code: 'custom', path: ['installRoot'], message: `${recipe.installRoot} holds other apps' files: name the app's own folder inside it.` });
        }
        if (recipe.installedPath !== undefined && !isInsideInstallRoot(recipe.installedPath, recipe.installRoot)) {
            ctx.addIssue({ code: 'custom', path: ['installedPath'], message: 'The installed file must be inside the install root.' });
        }
    }
}
export const AppProvisioningSchema = AppProvisioningObjectSchema.superRefine(checkProvisioning).openapi('AppProvisioning', {
    description: 'How a station installs an app into the player account (F-CAT-09, ADR-0045): a Store package registered for the player, or ' +
        'the vendor’s per-user installer — downloaded over HTTPS from allowed hosts, its Authenticode signer checked — run as the player.',
});
/** The player folders an app's sign-in data is under: `%LocalAppData%` or `%AppData%` (Store apps: `LocalAppData\Packages\<family>`). */
export const APP_DATA_ROOTS = ['local_app_data', 'app_data'];
export const AppDataRootSchema = z.enum(APP_DATA_ROOTS).openapi('AppDataRoot', {
    description: 'The player folder a sign-out path is relative to: `local_app_data` (%LocalAppData%) or `app_data` (%AppData%, roaming).',
});
/** At most this many sign-out paths on one app. */
export const MAX_SIGN_OUT_PATHS = 16;
/** A relative path under a player folder: backslashes, no drive, no leading separator, no `.`/`..` segment, no variables. */
const RELATIVE_PATH = /^(?![\\/])(?!.*(?:^|[\\/])\.\.?(?:[\\/]|$))[^%{}:*?"<>|]+$/;
/**
 * Where an app keeps a player's sign-in (F-CAT-09, spec/11 §5): a folder whose contents a session end clears, or a file it
 * deletes, relative to the player's `%LocalAppData%` or `%AppData%` — never outside the player's profile.
 */
export const AppSignOutPathSchema = z
    .object({
    root: AppDataRootSchema,
    path: z
        .string()
        .trim()
        .min(1)
        .max(260)
        .regex(RELATIVE_PATH, 'A path relative to the folder: no drive, no leading slash, no "..", no variables.')
        .openapi({ description: 'The path under the root folder.', example: 'discord\\Local Storage' }),
    /** `directory`: its contents are cleared (the folder is kept). `file`: it is deleted. */
    kind: z.enum(['directory', 'file']),
})
    .strict()
    .openapi('AppSignOutPath', {
    description: 'A folder or file under the player’s %LocalAppData% or %AppData% that holds an app’s sign-in; a session end clears it (spec/11 §5).',
});
/**
 * A recipe as it is stored: process names normalised as detection's are (lower case, `.exe` stripped), and the hosts,
 * processes and Run values de-duplicated in order, the expected blocked tools once each in the contract's order. Every other field is
 * kept as HQ wrote it.
 */
export function normaliseAppProvisioning(recipe) {
    const unique = (values, key = (value) => value) => {
        const out = [];
        for (const value of values)
            if (!out.some((held) => key(held) === key(value)))
                out.push(value);
        return out;
    };
    return {
        ...recipe,
        ...(recipe.allowedHosts === undefined ? {} : { allowedHosts: unique(recipe.allowedHosts) }),
        ...(recipe.closeProcesses === undefined ? {} : { closeProcesses: unique(recipe.closeProcesses.map(normaliseProcessName).filter((name) => name !== '')) }),
        ...(recipe.removeRunValues === undefined ? {} : { removeRunValues: unique(recipe.removeRunValues, (value) => value.toLowerCase()) }),
        ...(recipe.expectedBlockedChildren === undefined
            ? {}
            : { expectedBlockedChildren: PROVISIONING_CHILD_TOOLS.filter((tool) => recipe.expectedBlockedChildren?.includes(tool)) }),
    };
}
/** Sign-out paths as stored: one per root, path (case-insensitive) and kind, in order. */
export function normaliseSignOutPaths(paths) {
    const out = [];
    for (const path of paths) {
        const key = `${path.root}|${path.kind}|${path.path.toLowerCase()}`;
        if (!out.some((held) => `${held.root}|${held.kind}|${held.path.toLowerCase()}` === key))
            out.push({ root: path.root, path: path.path, kind: path.kind });
    }
    return out;
}
// ---------------------------------------------------------------------------------------------
// Hand edits, flags and image variants (F-CAT-01, F-CAT-07, F-HQ-16; spec/14 §6)
// ---------------------------------------------------------------------------------------------
//
// Every imported field is editable by hand (`PATCH /v1/hq/catalogue/games/{id}`), and a field HQ edited is
// recorded in `editedFields`: the weekly metadata refresh, a re-import and the on-demand refresh update only the
// fields **not** in it, so a human's edit is never silently overwritten (spec/14 §6). HQ can hand a field back to
// the provider (`providerFields`). `requiresElevation` (spec/11 §3.5) is a flag, not metadata: HQ sets it, a venue
// may turn it off for itself on its library entry, and the station routes such a title to the elevated service.
/** The metadata fields HQ may edit by hand, and so the names `editedFields` holds. */
export const GAME_EDITABLE_FIELDS = ['name', 'summary', 'genres', 'franchise', 'firstReleaseYear', 'publisher', 'ageRating', 'tags'];
export const GameEditableFieldSchema = z.enum(GAME_EDITABLE_FIELDS).openapi('GameEditableField', {
    description: 'A catalogue metadata field HQ may edit by hand; once edited, a refresh from the provider leaves it alone.',
});
/** The widths the worker makes WebP variants of a cover at (264 × 374 is the kiosk tile; 528 its 2× density). */
export const COVER_VARIANT_WIDTHS = [264, 528];
/** The widths the worker makes WebP variants of a hero at (half and full kiosk width). */
export const HERO_VARIANT_WIDTHS = [960, 1920];
/**
 * One resized copy of a cover or hero on our media CDN, made by the worker from the original (spec/03 §7.4,
 * spec/14 §6): WebP at a fixed width, so a client loads the size it displays rather than the original.
 */
export const ArtVariantSchema = z
    .object({
    width: z.int().positive(),
    url: z.string().url(),
})
    .openapi('ArtVariant', { description: 'A resized WebP copy of a cover or hero on our media CDN (spec/03 §7.4).' });
/** At most this many genres or tags on a title. */
export const MAX_GAME_GENRES = 20;
export const MAX_GAME_TAGS = 20;
/**
 * A title in the platform catalogue (spec/04 §5): a `game` — our record of an IGDB base game — or an `app`
 * HQ created by hand. The schema keeps its historical name; every game field and behaviour is unchanged.
 */
export const GameSchema = z
    .object({
    id: GameIdSchema,
    /** `game` (mirrored from IGDB) or `app` (HQ-created, no IGDB record). */
    kind: CatalogueKindSchema,
    /**
     * IGDB's id for this game, unique across the catalogue: the catalogue dedupes on it. `null` for an app,
     * which has no IGDB record — a game always has one.
     */
    igdbId: IgdbIdSchema.nullable(),
    /** The game's name, as IGDB has it at the time it was mirrored. */
    name: z.string(),
    /** IGDB's short summary. `null` when IGDB has none. */
    summary: z.string().nullable(),
    /** The cover reference (`coverImageId`), or `null` — though a mirrored base game always has a cover. */
    coverImageId: CoverImageIdSchema.nullable(),
    /**
     * Our own cached cover on the media CDN (`PUBLIC_MEDIA_URL`), fetched from IGDB server-side on
     * mirror (spec/14 §6). `null` when we hold no copy — object storage unconfigured, or not yet
     * cached — and a client then falls back to `coverImageId` resolved to IGDB's image CDN.
     */
    coverUrl: z.string().url().nullable(),
    /**
     * The square icon on our media CDN (spec/14 §6, ADR-0028) — an app's kiosk tile, uploaded by HQ or picked
     * from SteamGridDB and copied to our public-media bucket. `null` when there is none. For an **app** the
     * served `coverUrl` is this same URL, so every surface that shows a cover shows the icon.
     */
    iconUrl: z.string().url().nullable(),
    /** Where the icon came from — `steamgriddb` or `upload` — or `null` when there is none. A plain string, as `heroSource`. */
    iconSource: z.string().nullable(),
    /**
     * The kiosk category an app suggests (`AppCategory`: `apps`, `browsers`, `voice`, `media`, `tools`); the
     * venue library defaults to it when a venue adds the app. `null` for a game. A plain string on the wire.
     */
    suggestedCategory: z.string().nullable(),
    /** How a station recognises the title's process (spec/10 §7.3). Nothing but a launch until HQ sets it. */
    detection: TitleDetectionSchema,
    /**
     * How a station installs the app into the player account (F-CAT-09, ADR-0045), or `null` — always `null` for a game.
     * Only HQ writes it; a venue turns it on for its PCs with its library entry's `installForPlayers`.
     */
    provisioning: AppProvisioningSchema.nullable(),
    /** Where the app keeps a player's sign-in, cleared at every session end (F-CAT-09, spec/11 §5). Empty for a game. */
    signOutPaths: z.array(AppSignOutPathSchema),
    /** The genre names IGDB lists, in IGDB's order. Empty when it lists none. */
    genres: z.array(z.string()),
    /** The franchise the game belongs to, if IGDB names one. `null` otherwise. */
    franchise: z.string().nullable(),
    /** The year the base game first released, from IGDB's first-release date. `null` when IGDB has none. */
    firstReleaseYear: z.int().nullable(),
    /**
     * How a station launches this game (F-CAT-05, spec/10 §5.2): HQ's curated recipes, one or two per
     * launcher. Empty until HQ configures them — a mirrored game carries none, so the default preserves
     * the pre-F-CAT-05 behaviour. The venue library carries these to the station in its signed bundle.
     */
    launchRecipes: z.array(LaunchRecipeSchema),
    /**
     * The wide hero art (≈ 1920 × 620) on our media CDN, which HQ picked from SteamGridDB or IGDB (or the
     * `catalog.cache_art` job defaulted) and the server copied into our public-media bucket (spec/14 §6,
     * ADR-0028). `null` when there is none — never a third-party URL.
     */
    heroUrl: z.string().url().nullable(),
    /**
     * Where the hero came from — an `ArtSource` (`steamgriddb`, `igdb`) — or `null` when there is none. A
     * plain nullable string on the wire: the client generators carry no nullable enums.
     */
    heroSource: z.string().nullable(),
    /** The game's logo (a transparent PNG) on our media CDN, or `null` (spec/14 §6, ADR-0028). */
    logoUrl: z.string().url().nullable(),
    /** Where the logo came from — an `ArtSource` — or `null` when there is none. A plain string, as `heroSource`. */
    logoSource: z.string().nullable(),
    /** The publisher, from IGDB's involved companies (or typed by HQ), or `null`. */
    publisher: z.string().nullable(),
    /**
     * The age rating as a label — `PEGI 16`, `ESRB M` — from IGDB's age ratings (PEGI preferred) or typed by HQ, or
     * `null` when there is none.
     */
    ageRating: z.string().nullable(),
    /** HQ's own tags for the title (never imported). Empty when it has none. */
    tags: z.array(z.string()),
    /**
     * The title needs administrator rights to run (spec/11 §3.5): the station routes its launch to the elevated
     * service, never runs it elevated itself. Set only by HQ; a venue may turn it off for itself (its library entry's
     * `elevationDisabled`). Default `false`.
     */
    requiresElevation: z.boolean(),
    /**
     * When HQ archived the title, or `null`. An archived title is hidden from the venues' "add to library" browse;
     * the library entries that already offer it keep working. Never hard-deleted.
     */
    archivedAt: IsoDateTimeSchema.nullable(),
    /** The fields HQ edited by hand — a refresh from the provider leaves these alone (spec/14 §6). */
    editedFields: z.array(GameEditableFieldSchema),
    /** WebP variants of our cached cover, narrowest first; empty until the worker has made them (or for an app). */
    coverVariants: z.array(ArtVariantSchema),
    /** WebP variants of the hero, narrowest first; empty until the worker has made them. */
    heroVariants: z.array(ArtVariantSchema),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Game', { description: 'A title in the platform catalogue — HQ’s mirror of an IGDB base game, or an app HQ added (spec/04 §5, spec/14 §6).' });
export const GameListSchema = paginated(GameSchema, 'GameList');
/**
 * One base-game hit from a catalogue search: what HQ needs to recognise the game and decide whether
 * to mirror it. Not our record — nothing is written until HQ picks it — so it is keyed by `igdbId`,
 * and it says whether that game is `alreadyInCatalogue` (and if so, its `gameId`) so the search UI
 * can show what is already mirrored.
 */
export const CatalogueSearchResultSchema = z
    .object({
    igdbId: IgdbIdSchema,
    name: z.string(),
    summary: z.string().nullable(),
    coverImageId: CoverImageIdSchema.nullable(),
    firstReleaseYear: z.int().nullable(),
    /** Is this game already mirrored into the catalogue? Its `gameId` is set when it is. */
    alreadyInCatalogue: z.boolean(),
    gameId: GameIdSchema.nullable(),
})
    .openapi('CatalogueSearchResult', {
    description: 'A base-game candidate from IGDB, and whether the catalogue already holds it (spec/14 §6).',
});
/**
 * The results of a catalogue search: a bounded, most-popular-first list of base games, straight from
 * the provider. Not the cursor-paginated envelope — it is a provider proxy answer, not a collection
 * of our rows — so it is a plain `{ data }` object (spec/08 §2.4 wraps our collections; this is not one).
 */
export const CatalogueSearchResultsSchema = z
    .object({ data: z.array(CatalogueSearchResultSchema) })
    .openapi('CatalogueSearchResults', { description: 'Base-game candidates for a catalogue search, most popular first.' });
/** The query of `GET /v1/hq/catalogue/search`: the text to search IGDB for. */
export const CatalogueSearchQuerySchema = z
    .object({
    q: z.string().trim().min(1).max(120).openapi({ description: 'The title to search IGDB for.', example: 'half-life' }),
})
    .openapi('CatalogueSearchQuery');
/**
 * The body of `POST /v1/hq/catalogue/games`: the IGDB id HQ picked. Resolved to its base-game root,
 * deduped on `igdbId`, and mirrored. The same request is the one-click accept for a detected match.
 */
export const MirrorGameSchema = z
    .object({ igdbId: IgdbIdSchema })
    .strict()
    .openapi('MirrorGame', { description: 'Mirror the IGDB base game with this id into the catalogue.' });
/** The path of `POST /v1/hq/catalogue/games/{id}/refresh`: our own catalogue id (`gam_…`). */
export const RefreshGameParamsSchema = z
    .object({ id: GameIdSchema })
    .openapi('RefreshGameParams', { description: 'The catalogue game to refresh from IGDB (spec/14 §6, F-CAT-07).' });
/** The path of `PUT /v1/hq/catalogue/games/{id}/launch-recipes`: our own catalogue id (`gam_…`). */
export const GameLaunchRecipesParamsSchema = z
    .object({ id: GameIdSchema })
    .openapi('GameLaunchRecipesParams', { description: 'The catalogue game whose launch recipes to set (F-CAT-05, spec/10 §5.2).' });
/**
 * The body of `PUT /v1/hq/catalogue/games/{id}/launch-recipes`: the full set of recipes, which
 * **replaces** whatever the game held (PUT, not PATCH). An empty array clears them.
 */
export const SetLaunchRecipesSchema = z
    .object({ recipes: z.array(LaunchRecipeSchema).max(MAX_LAUNCH_RECIPES) })
    .strict()
    .openapi('SetLaunchRecipes', { description: 'Replace a catalogue game’s launch recipes (F-CAT-05, spec/10 §5.2).' });
// ---------------------------------------------------------------------------------------------
// Trailers — manual HQ uploads (spec/14 §6, F-CAT-01/-07)
// ---------------------------------------------------------------------------------------------
/**
 * The video types a trailer may be. **Manual HQ uploads, not fetched from IGDB** (the product owner,
 * 2026-09-22): the file is PUT straight to our object storage and served from our media CDN. MP4
 * (H.264/AAC) plays everywhere; WebM (VP9) is the smaller open alternative. No HLS/DASH manifest and
 * no `video/quicktime`: a trailer is one self-contained file a browser `<video>` can play.
 */
export const TRAILER_CONTENT_TYPES = ['video/mp4', 'video/webm'];
export const TrailerContentTypeSchema = z.enum(TRAILER_CONTENT_TYPES).openapi('TrailerContentType', {
    description: 'The video container a trailer upload may be: `video/mp4` or `video/webm`.',
});
/** 512 MB. A short marketing trailer at 1080p is tens of MB; this is headroom, not a target. */
export const MAX_TRAILER_UPLOAD_BYTES = 512 * 1024 * 1024;
export const TRAILER_STATUSES = ['pending', 'ready', 'failed'];
export const TrailerStatusSchema = z.enum(TRAILER_STATUSES).openapi('TrailerStatus', {
    description: '`pending` until the upload is completed, `ready` once the server has checked the object is there and is the ' +
        'declared size and type, `failed` if it was missing or not what was declared.',
});
/** A trailer video an HQ user uploaded for a catalogue game (spec/14 §6, F-CAT-01/-07). */
export const GameTrailerSchema = z
    .object({
    id: GameTrailerIdSchema,
    /** The catalogue game this trailer belongs to. */
    gameId: GameIdSchema,
    /** HQ's label for the trailer, e.g. "Launch trailer" or "Gameplay reveal". */
    title: z.string(),
    contentType: TrailerContentTypeSchema,
    bytes: z.int().positive(),
    status: TrailerStatusSchema,
    /**
     * Where the trailer is served from on our media CDN (`PUBLIC_MEDIA_URL`). `null` while the upload
     * is `pending` or `failed`, or when object storage is not configured — a `ready` trailer always
     * has one.
     */
    url: z.string().url().nullable(),
    /**
     * Whether the kiosk's lock-screen playlist plays this trailer (F-KIO-01, F-CAT-07). A new upload is in the loop;
     * HQ takes one out on the Media sheet. Only a `ready` trailer in the loop rides a station's library bundle.
     */
    inKioskLoop: z.boolean(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('GameTrailer', { description: 'A trailer video an HQ user uploaded for a catalogue game (spec/14 §6).' });
/**
 * The trailers of one game, newest first. A plain `{ data }` list, not the cursor-paginated envelope:
 * a game has a handful of trailers, never a page of them (spec/08 §2.4 wraps our large collections).
 */
export const GameTrailerListSchema = z
    .object({ data: z.array(GameTrailerSchema) })
    .openapi('GameTrailerList', { description: 'Every trailer of a catalogue game, newest first.' });
/** The body of `POST /v1/hq/catalogue/games/{id}/trailers`: the trailer to reserve and upload. */
export const CreateGameTrailerSchema = z
    .object({
    title: z.string().trim().min(1).max(120).openapi({ description: 'HQ’s label for the trailer.', example: 'Launch trailer' }),
    contentType: TrailerContentTypeSchema,
    bytes: z.int().positive().max(MAX_TRAILER_UPLOAD_BYTES),
})
    .strict()
    .openapi('CreateGameTrailer', { description: 'Reserve a trailer for a catalogue game and get a signed URL to upload it.' });
/** Where and how to PUT the trailer bytes: a signed URL, and the headers its signature covers. */
export const TrailerSignedUploadSchema = z
    .object({
    method: z.enum(['PUT']),
    url: z.string().url(),
    /** Send exactly this with the PUT; the signature covers it, and the body's length. */
    contentType: TrailerContentTypeSchema,
    expiresAt: IsoDateTimeSchema,
})
    .openapi('TrailerSignedUpload');
/** The answer to reserving a trailer: the pending record, and where to upload the bytes. */
export const GameTrailerUploadSchema = z
    .object({ trailer: GameTrailerSchema, upload: TrailerSignedUploadSchema })
    .openapi('GameTrailerUpload');
/** The path of a game's trailers collection: our own catalogue id (`gam_…`). */
export const GameTrailersParamsSchema = z
    .object({ id: GameIdSchema })
    .openapi('GameTrailersParams', { description: 'The catalogue game whose trailers these are (spec/14 §6).' });
/** The path of one trailer of a game: the game id (`gam_…`) and the trailer id (`gtr_…`). */
export const GameTrailerParamsSchema = z
    .object({ id: GameIdSchema, trailerId: GameTrailerIdSchema })
    .openapi('GameTrailerParams', { description: 'One trailer of a catalogue game (spec/14 §6).' });
// ---------------------------------------------------------------------------------------------
// Game media — art candidates and picks (spec/14 §6, ADR-0028)
// ---------------------------------------------------------------------------------------------
/**
 * One piece of art HQ may pick for a game: a thumbnail to show, the full-size image the server would
 * copy, and what the source says about it. `sourceId` is the source's own id for the image (SteamGridDB's
 * asset id, IGDB's `image_id`); picking sends it back and the server **re-resolves** it against the
 * source — it never downloads a URL a client sent. Both URLs are the source's own CDN, for HQ's
 * preview only; nothing a player sees ever loads them.
 */
export const ArtCandidateSchema = z
    .object({
    source: ArtSourceSchema,
    sourceId: z.string().min(1).max(128),
    /** A small preview on the source's CDN, for the HQ picker. */
    url: z.string().url(),
    /** The full-size image the server copies into our storage on pick. */
    fullUrl: z.string().url(),
    width: z.int().nonnegative().nullable(),
    height: z.int().nonnegative().nullable(),
    /** SteamGridDB's style (`alternate`, `official`, `blurred`, …), or `null` for IGDB. */
    style: z.string().nullable(),
    /** The source's ranking signal (SteamGridDB's net votes), higher first; `null` for IGDB. */
    score: z.int().nullable(),
})
    .openapi('ArtCandidate', { description: 'A hero or logo HQ may pick for a catalogue game (spec/14 §6).' });
/**
 * The candidates for one kind of art, best first, SteamGridDB before IGDB. `unavailableSources` names
 * a source that is not configured or could not answer, so HQ sees why a grid is thin rather than an
 * error — the other source's candidates still come back.
 */
export const ArtCandidateListSchema = z
    .object({
    data: z.array(ArtCandidateSchema),
    unavailableSources: z.array(ArtSourceSchema),
})
    .openapi('ArtCandidateList', { description: 'The hero or logo candidates for a catalogue game, best first (spec/14 §6).' });
/** The query of `GET /v1/hq/catalogue/games/{id}/art-candidates`: which kind of art. */
export const ArtCandidatesQuerySchema = z.object({ kind: ArtKindSchema }).openapi('ArtCandidatesQuery');
/** The path of `GET …/art-candidates`: our own catalogue id (`gam_…`). */
export const GameArtCandidatesParamsSchema = z
    .object({ id: GameIdSchema })
    .openapi('GameArtCandidatesParams', { description: 'The catalogue game whose art candidates to list (spec/14 §6).' });
/** The path of `PUT`/`DELETE /v1/hq/catalogue/games/{id}/art/{kind}`. */
export const GameArtParamsSchema = z
    .object({ id: GameIdSchema, kind: ArtKindSchema })
    .openapi('GameArtParams', { description: 'One kind of art on a catalogue game (spec/14 §6).' });
/** The body of `PUT /v1/hq/catalogue/games/{id}/art/{kind}`: the candidate HQ picked. */
export const PickGameArtSchema = z
    .object({ source: ArtSourceSchema, sourceId: z.string().trim().min(1).max(128) })
    .strict()
    .openapi('PickGameArt', { description: 'Pick a hero or logo candidate; the server copies it into our storage (spec/14 §6).' });
/** A hero is at most 8 MB, a logo at most 4 MB, an icon 1 MB: the server refuses a larger image rather than buffer it. */
export const MAX_HERO_BYTES = 8 * 1024 * 1024;
export const MAX_LOGO_BYTES = 4 * 1024 * 1024;
export const MAX_ICON_BYTES = 1024 * 1024;
// ---------------------------------------------------------------------------------------------
// Apps — non-game titles HQ adds by hand (spec/14 §6; F-CAT-01, F-CAT-02)
// ---------------------------------------------------------------------------------------------
//
// An app (a browser, voice chat, a music player, a game launcher) has no IGDB record: HQ creates it with a
// name, the kiosk category it suggests, its launch recipes and its detection rules, and gives it a square
// icon — uploaded (PNG/WebP, over a signed URL, copied to a content-addressed key) or picked from
// SteamGridDB's icons (`GET …/games/{id}/art-candidates?kind=icon`, `PUT …/games/{id}/art/icon`). All media
// lands in our public-media bucket (ADR-0028); nothing is hotlinked. "Add common apps" creates the usual
// set (browsers, Discord, Spotify, the game launchers…) where the catalogue does not hold them yet.
/** At most this long: an app's name, as the kiosk tile shows it. */
const appNameSchema = z.string().trim().min(1).max(120).openapi({ description: 'The app’s name, as players see it.', example: 'Discord' });
/** At most this long: an app's one-paragraph description. */
const appSummarySchema = z.string().trim().min(1).max(2000).openapi({ description: 'A short description of the app.' });
/** The body of `POST /v1/hq/catalogue/apps`: a new app. Its name is unique among apps (case-insensitive). */
export const CreateCatalogueAppSchema = z
    .object({
    name: appNameSchema,
    summary: appSummarySchema.nullable().optional(),
    /** The kiosk category a venue most likely files it under; the venue library defaults to it. */
    suggestedCategory: AppCategorySchema,
    /** How a station launches it — the same recipes a game carries (spec/10 §5.2). May be empty. */
    launchRecipes: z.array(LaunchRecipeSchema).max(MAX_LAUNCH_RECIPES).optional(),
    /** How a station recognises its process (spec/10 §7.3). Absent: nothing but a launch. */
    detection: TitleDetectionInputSchema.optional(),
    /** How a station installs it into the player account (F-CAT-09, ADR-0045). Absent or `null`: it cannot be. */
    provisioning: AppProvisioningSchema.nullable().optional(),
    /** Where it keeps a player's sign-in, cleared at every session end (spec/11 §5). Absent: none. */
    signOutPaths: z.array(AppSignOutPathSchema).max(MAX_SIGN_OUT_PATHS).optional(),
})
    .strict()
    .openapi('CreateCatalogueApp', { description: 'Add an app (not a game) to the platform catalogue (spec/14 §6).' });
/**
 * The body of `PATCH /v1/hq/catalogue/apps/{id}`: only the fields sent change. `launchRecipes` and `signOutPaths` replace
 * the set; `provisioning: null` removes the recipe (the venues that turned Install for players on stop installing it).
 */
export const UpdateCatalogueAppSchema = z
    .object({
    name: appNameSchema,
    summary: appSummarySchema.nullable(),
    suggestedCategory: AppCategorySchema,
    launchRecipes: z.array(LaunchRecipeSchema).max(MAX_LAUNCH_RECIPES),
    detection: TitleDetectionInputSchema,
    provisioning: AppProvisioningSchema.nullable(),
    signOutPaths: z.array(AppSignOutPathSchema).max(MAX_SIGN_OUT_PATHS),
})
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one field to change.' })
    .openapi('UpdateCatalogueApp', { description: 'Edit an app in the platform catalogue — only the fields sent change (spec/14 §6).' });
/** The path of an app's routes: our own catalogue id (`gam_…`), which must name an app. */
export const CatalogueAppParamsSchema = z
    .object({ id: GameIdSchema })
    .openapi('CatalogueAppParams', { description: 'An app in the platform catalogue (spec/14 §6).' });
/** The answer to "Add common apps": every common app, and how many this call created. */
export const SeedCommonAppsResultSchema = z
    .object({
    /** Every common app, as the catalogue now holds it — created by this call or already there. */
    data: z.array(GameSchema),
    /** How many of them this call created. `0` when the catalogue already held them all. */
    created: z.int().nonnegative(),
})
    .openapi('SeedCommonAppsResult', { description: 'The common apps the catalogue now holds, and how many were just added (spec/14 §6).' });
/** The image types an uploaded icon may be: PNG or WebP (transparency, and what a kiosk tile renders). */
export const ICON_CONTENT_TYPES = ['image/png', 'image/webp'];
export const IconContentTypeSchema = z.enum(ICON_CONTENT_TYPES).openapi('IconContentType', {
    description: 'The image type an uploaded app icon may be: `image/png` or `image/webp`.',
});
/** The body of `POST /v1/hq/catalogue/apps/{id}/icon`: the icon about to be uploaded. */
export const CreateAppIconUploadSchema = z
    .object({ contentType: IconContentTypeSchema, bytes: z.int().positive().max(MAX_ICON_BYTES) })
    .strict()
    .openapi('CreateAppIconUpload', { description: 'Reserve an icon upload for an app and get a signed URL to PUT it to.' });
/** Where and how to PUT an icon's bytes: a signed URL, and the headers its signature covers. */
export const IconSignedUploadSchema = z
    .object({
    method: z.enum(['PUT']),
    url: z.string().url(),
    /** Send exactly this with the PUT; the signature covers it, and the body's length. */
    contentType: IconContentTypeSchema,
    expiresAt: IsoDateTimeSchema,
})
    .openapi('IconSignedUpload');
/** The answer to reserving an icon upload: its id (to complete it with) and where to PUT the bytes. */
export const AppIconUploadSchema = z
    .object({
    /** The upload's id; `POST …/icon/{uploadId}/complete` confirms it. */
    uploadId: z.uuid(),
    upload: IconSignedUploadSchema,
})
    .openapi('AppIconUpload');
/** The path of `POST …/apps/{id}/icon/{uploadId}/complete`. */
export const AppIconUploadParamsSchema = z
    .object({ id: GameIdSchema, uploadId: z.uuid() })
    .openapi('AppIconUploadParams', { description: 'One icon upload of an app (spec/14 §6).' });
/** The query of `GET /v1/hq/catalogue/games`: a page, optionally only games or only apps. */
export const ListCatalogueGamesQuerySchema = PageQuerySchema.extend({
    kind: CatalogueKindSchema.optional().openapi({ description: 'Only titles of this kind — `game` or `app`. Absent: both.' }),
}).openapi('ListCatalogueGamesQuery');
/** The path of `PUT /v1/hq/catalogue/games/{id}/detection`: our own catalogue id (`gam_…`), a game or an app. */
export const TitleDetectionParamsSchema = z
    .object({ id: GameIdSchema })
    .openapi('TitleDetectionParams', { description: 'The catalogue title whose detection rules to set (spec/10 §7.3).' });
// ---------------------------------------------------------------------------------------------
// Editing, hand-creating and archiving a game; detection testing (F-CAT-01, F-CAT-07, F-HQ-16)
// ---------------------------------------------------------------------------------------------
const gameNameSchema = z.string().trim().min(1).max(200).openapi({ description: 'The game’s name, as players see it.', example: 'Half-Life 2' });
const gameSummarySchema = z.string().trim().min(1).max(4000);
const gameLabelSchema = z.string().trim().min(1).max(60);
const releaseYearSchema = z.int().min(1950).max(2100);
const publisherSchema = z.string().trim().min(1).max(200);
const franchiseSchema = z.string().trim().min(1).max(200);
const ageRatingSchema = z.string().trim().min(1).max(32).openapi({ description: 'An age rating label, e.g. `PEGI 16` or `ESRB M`.', example: 'PEGI 16' });
/**
 * The body of `PATCH /v1/hq/catalogue/games/{id}`: only the fields sent change. Each metadata field sent is recorded
 * as hand-edited (`editedFields`) so no refresh overwrites it; `providerFields` hands fields back to the provider (the
 * next refresh updates them again). `requiresElevation` is the flag (spec/11 §3.5), not metadata.
 */
export const UpdateCatalogueGameSchema = z
    .object({
    name: gameNameSchema,
    summary: gameSummarySchema.nullable(),
    genres: z.array(gameLabelSchema).max(MAX_GAME_GENRES),
    franchise: franchiseSchema.nullable(),
    firstReleaseYear: releaseYearSchema.nullable(),
    publisher: publisherSchema.nullable(),
    ageRating: ageRatingSchema.nullable(),
    tags: z.array(gameLabelSchema).max(MAX_GAME_TAGS),
    requiresElevation: z.boolean(),
    /** Fields to hand back to the metadata provider: no longer hand-edited, so the next refresh updates them. */
    providerFields: z.array(GameEditableFieldSchema).max(GAME_EDITABLE_FIELDS.length),
})
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one field to change.' })
    .openapi('UpdateCatalogueGame', { description: 'Edit a catalogue game — only the fields sent change; each is kept through refreshes (spec/14 §6).' });
/**
 * The body of `POST /v1/hq/catalogue/games/manual`: a game HQ creates by hand — when the metadata provider is down, or
 * does not know the title. It has no `igdbId` (so no refresh ever touches it) and no cover until art is picked.
 */
export const CreateCatalogueGameSchema = z
    .object({
    name: gameNameSchema,
    summary: gameSummarySchema.nullable().optional(),
    genres: z.array(gameLabelSchema).max(MAX_GAME_GENRES).optional(),
    franchise: franchiseSchema.nullable().optional(),
    firstReleaseYear: releaseYearSchema.nullable().optional(),
    publisher: publisherSchema.nullable().optional(),
    ageRating: ageRatingSchema.nullable().optional(),
    tags: z.array(gameLabelSchema).max(MAX_GAME_TAGS).optional(),
    requiresElevation: z.boolean().optional(),
})
    .strict()
    .openapi('CreateCatalogueGame', { description: 'Create a catalogue game by hand, with no metadata provider record (spec/14 §6).' });
/** The path of a catalogue game's routes: our own catalogue id (`gam_…`). */
export const CatalogueGameParamsSchema = z
    .object({ id: GameIdSchema })
    .openapi('CatalogueGameParams', { description: 'A title in the platform catalogue (spec/14 §6).' });
/** Where a catalogue title is offered across the Platform — what archiving it would warn about. */
export const CatalogueGameUsageSchema = z
    .object({
    /** How many venues' libraries offer it. Their entries keep working if it is archived. */
    venues: z.int().nonnegative(),
    /** How many organisations those venues belong to. */
    organizations: z.int().nonnegative(),
})
    .openapi('CatalogueGameUsage', { description: 'How many venue libraries offer a catalogue title (spec/14 §6).' });
/** The body of `POST /v1/hq/catalogue/detection/test`: a pasted process list, one per line. */
export const DetectionTestRequestSchema = z
    .object({
    /**
     * The processes, one per line: a bare name (`cs2.exe`), a full image path, or a `tasklist /fo csv` row. Blank lines
     * are skipped; at most `MAX_DETECTION_TEST_LINES` processes are read.
     */
    processes: z.string().max(200_000),
})
    .strict()
    .openapi('DetectionTestRequest', { description: 'A pasted process list to test against the catalogue’s detection rules (F-HQ-16).' });
/** One title a pasted process matched, and why. */
export const DetectionTestMatchSchema = z
    .object({
    gameId: GameIdSchema,
    name: z.string(),
    kind: CatalogueKindSchema,
    /** The title is archived — venues already offering it still detect it. */
    archived: z.boolean(),
    /** `catalogue` (its own rules), `recipe` (its exe recipe's file name; it has no rules) or `default` (the curated names). */
    via: z.enum(DETECTION_TEST_VIAS),
    /** The rules that held: every rule a `catalogue` match sets, or the one a `recipe`/`default` match used. */
    rules: z.array(z.enum(DETECTION_TEST_RULES)),
})
    .openapi('DetectionTestMatch', { description: 'A catalogue title a pasted process matched, and the rules that held (F-HQ-16).' });
/** One pasted process: what was read from its line, and every title it matched. */
export const DetectionTestLineSchema = z
    .object({
    input: z.string(),
    /** The process name compared: lower case, `.exe` stripped. */
    processName: z.string(),
    /** The full image path, when the line was one; `null` for a bare name (a path pattern or regex then cannot hold). */
    path: z.string().nullable(),
    matches: z.array(DetectionTestMatchSchema),
})
    .openapi('DetectionTestLine', { description: 'A pasted process and the catalogue titles it matched (F-HQ-16).' });
export const DetectionTestResultSchema = z
    .object({
    /** Each process read, in the paste's order (at most `MAX_DETECTION_TEST_LINES`). */
    lines: z.array(DetectionTestLineSchema),
    /** How many distinct titles matched at least one process. */
    matchedTitles: z.int().nonnegative(),
})
    .openapi('DetectionTestResult', { description: 'Which catalogue titles a pasted process list would match on a station, and why (F-HQ-16).' });
export { MAX_DETECTION_TEST_LINES };
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const json = (schema) => ({
    body: { content: { 'application/json': { schema } }, required: true },
});
const forbidden = problemResponse('Not HQ, no `hq:catalog`, or no second factor on the session.', 'forbidden', 'second-factor-required');
const providerUnavailable = problemResponse('The IGDB metadata provider is not configured or cannot answer.', 'catalogue-provider-unavailable');
export const searchCatalogueRoute = createRoute({
    method: 'get',
    path: '/v1/hq/catalogue/search',
    operationId: 'searchCatalogue',
    tags: ['hq'],
    summary: 'Search IGDB for base games to mirror',
    description: 'Searches the metadata provider (IGDB) for base games — `category = main_game` and no `version_parent`, with a cover, ' +
        'most popular first — and says for each whether the catalogue already holds it. Never DLC, editions or bundles. Needs ' +
        'the provider configured; without it, `503 catalogue-provider-unavailable`. Platform scope: requires `hq:catalog` and ' +
        'a second factor. Nothing is written; mirroring a result is `POST /v1/hq/catalogue/games`.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { query: CatalogueSearchQuerySchema },
    responses: {
        200: jsonResponse(CatalogueSearchResultsSchema, 'The base games IGDB matched, most popular first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        503: providerUnavailable,
        500: commonProblemResponses[500],
    },
});
export const mirrorCatalogueGameRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/games',
    operationId: 'mirrorCatalogueGame',
    tags: ['hq'],
    summary: 'Mirror a base game into the catalogue',
    description: 'Reads the picked game from IGDB, resolves it up to its base-game root (following `version_parent` and `parent_game`), ' +
        'and mirrors that base game into the catalogue: our own `gam_…` id, the source `igdbId`, and the metadata we keep. The ' +
        'catalogue dedupes on `igdbId` — if the base game is already mirrored, the existing row is returned with `200` rather ' +
        'than a second one, so a retry is safe. A pick that resolves to no base game, or a base game with no cover, is `422 ' +
        'not-a-base-game`. Needs the provider configured; without it, `503 catalogue-provider-unavailable`. This is also the ' +
        'one-click accept for an auto-detected-but-unmatched game once station detection feeds it (F-CAT-04). Platform scope: ' +
        'requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { ...json(MirrorGameSchema) },
    responses: {
        200: jsonResponse(GameSchema, 'The game was already mirrored; the existing record.'),
        201: jsonResponse(GameSchema, 'The game just mirrored into the catalogue.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        422: problemResponse('The pick does not resolve to a base game with a cover.', 'not-a-base-game'),
        429: commonProblemResponses[429],
        503: providerUnavailable,
        500: commonProblemResponses[500],
    },
});
export const refreshCatalogueGameRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/games/{id}/refresh',
    operationId: 'refreshCatalogueGame',
    tags: ['hq'],
    summary: 'Refresh a mirrored game from IGDB',
    description: 'Re-fetches the base game from IGDB and updates the mirrored record — name, summary, genres, franchise and ' +
        'first-release year — and **re-uploads the cover** to our object storage, so `coverUrl` reflects the current ' +
        'art (a changed cover gets a new content-addressed key, busting the CDN cache). This is F-CAT-07’s on-demand ' +
        'metadata-and-art refresh; the bulk weekly refresh is the flagged follow-up. Idempotent — refreshing an ' +
        'unchanged game rewrites the same record and the same object. The refresh is audited: one platform-level row. ' +
        'A game id nobody holds is `404`; a game that no longer resolves to a base game with a cover is `422 ' +
        'not-a-base-game`. Needs the provider configured; without it, `503 catalogue-provider-unavailable`. Platform ' +
        'scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: RefreshGameParamsSchema },
    responses: {
        200: jsonResponse(GameSchema, 'The refreshed game.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No game in the catalogue has that id.', 'not-found'),
        422: problemResponse('The game no longer resolves to a base game with a cover.', 'not-a-base-game'),
        429: commonProblemResponses[429],
        503: providerUnavailable,
        500: commonProblemResponses[500],
    },
});
export const listCatalogueGamesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/catalogue/games',
    operationId: 'listCatalogueGames',
    tags: ['hq'],
    summary: 'Every title in the catalogue, newest first',
    description: 'The catalogue’s own rows — mirrored games and HQ-added apps, newest first; `kind` narrows it to one. No ' +
        'provider call. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { query: ListCatalogueGamesQuerySchema },
    responses: {
        200: jsonResponse(GameListSchema, 'A page of catalogue titles — games and apps unless `kind` narrows it.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const setCatalogueGameLaunchRecipesRoute = createRoute({
    method: 'put',
    path: '/v1/hq/catalogue/games/{id}/launch-recipes',
    operationId: 'setCatalogueGameLaunchRecipes',
    tags: ['hq'],
    summary: "Set a catalogue game's launch recipes",
    description: 'Replaces the game’s launch recipes with the set sent — how a station launches it (F-CAT-05, spec/10 §5.2): by ' +
        '`exe`, launcher `uri`, Store `aumid`, or a `lnk` shortcut. HQ suggests one recipe per launcher; the venue library ' +
        'carries them to the station in its signed bundle. An empty array clears them. Reads and writes our own `game` ' +
        'table — no provider call. The change is audited: one platform-level row (`catalog.game.launch_recipes.set`). A ' +
        'game id nobody holds is `404`. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameLaunchRecipesParamsSchema, ...json(SetLaunchRecipesSchema) },
    responses: {
        200: jsonResponse(GameSchema, 'The game with its new launch recipes.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No game in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const trailerForbidden = problemResponse('Not HQ, no `hq:catalog`, or no second factor on the session.', 'forbidden', 'second-factor-required');
export const createGameTrailerRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/games/{id}/trailers',
    operationId: 'createGameTrailer',
    tags: ['hq'],
    summary: 'Reserve a trailer for a catalogue game and get a signed upload URL',
    description: 'Reserves a trailer record (`pending`) for the game and returns a short-lived signed URL to PUT the video to our ' +
        'object storage — the bytes never pass through the api, exactly as a venue photo does (spec/08 §4.19). The ' +
        'signature covers the declared `Content-Type` and `Content-Length`, so an upload of another type or size is ' +
        'refused by the store. **Trailers are manual HQ uploads, not fetched from IGDB** (the product owner, 2026-09-22). ' +
        'The upload is confirmed with `POST …/trailers/{trailerId}/complete`. A game id nobody holds is `404`; object ' +
        'storage not configured answers `500`, as an upload does anywhere (spec/20 §1.2). Platform scope: requires ' +
        '`hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameTrailersParamsSchema, ...json(CreateGameTrailerSchema) },
    responses: {
        201: { description: 'The trailer, pending, and where to upload it.', content: { [JSON_CONTENT_TYPE]: { schema: GameTrailerUploadSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: trailerForbidden,
        404: problemResponse('No game in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listGameTrailersRoute = createRoute({
    method: 'get',
    path: '/v1/hq/catalogue/games/{id}/trailers',
    operationId: 'listGameTrailers',
    tags: ['hq'],
    summary: 'Every trailer of a catalogue game, newest first',
    description: 'The game’s trailers — reads our own rows, no provider call. A `ready` trailer carries its `url` on our media CDN; ' +
        'a `pending` or `failed` one has `url: null`. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameTrailersParamsSchema },
    responses: {
        200: jsonResponse(GameTrailerListSchema, 'The game’s trailers, newest first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: trailerForbidden,
        404: problemResponse('No game in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const completeGameTrailerRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/games/{id}/trailers/{trailerId}/complete',
    operationId: 'completeGameTrailer',
    tags: ['hq'],
    summary: 'Confirm a trailer upload',
    description: 'Checks the object is in storage and is the declared size and type, then marks the trailer `ready` and serves it ' +
        'from our media CDN. Adding a trailer is audited: one platform-level row (`catalog.trailer.add`). An object that ' +
        'is missing, the wrong size or the wrong type is `422`, and the trailer is marked `failed` and its object removed ' +
        '— our public media never keeps something that is not what it declared. A game or trailer id nobody holds is ' +
        '`404`. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameTrailerParamsSchema },
    responses: {
        200: jsonResponse(GameTrailerSchema, 'The trailer, ready.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: trailerForbidden,
        404: problemResponse('No such game or trailer in the catalogue.', 'not-found'),
        422: problemResponse('The upload is missing, or not the declared size or type.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** The body of `PATCH /v1/hq/catalogue/games/{id}/trailers/{trailerId}`: what HQ changes on a trailer. */
export const UpdateGameTrailerSchema = z
    .object({
    inKioskLoop: z.boolean().openapi({ description: 'Whether the kiosk’s lock-screen playlist plays this trailer.' }),
})
    .strict()
    .openapi('UpdateGameTrailer', { description: 'Put a trailer in, or take it out of, the kiosk’s lock-screen playlist.' });
export const updateGameTrailerRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/catalogue/games/{id}/trailers/{trailerId}',
    operationId: 'updateGameTrailer',
    tags: ['hq'],
    summary: 'Put a trailer in or out of the kiosk loop',
    description: 'Sets whether the kiosk’s lock-screen playlist plays this trailer (F-KIO-01, F-CAT-07). Stations pick the change ' +
        'up with their next library pull. Audited: one platform-level row (`catalog.trailer.update`). A game or trailer id ' +
        'nobody holds is `404`. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameTrailerParamsSchema, ...json(UpdateGameTrailerSchema) },
    responses: {
        200: jsonResponse(GameTrailerSchema, 'The trailer, updated.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: trailerForbidden,
        404: problemResponse('No such game or trailer in the catalogue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const deleteGameTrailerRoute = createRoute({
    method: 'delete',
    path: '/v1/hq/catalogue/games/{id}/trailers/{trailerId}',
    operationId: 'deleteGameTrailer',
    tags: ['hq'],
    summary: 'Remove a trailer from a catalogue game',
    description: 'Deletes the trailer record and its object from our storage. Removing a trailer is audited: one platform-level ' +
        'row (`catalog.trailer.remove`). A game or trailer id nobody holds is `404`. Platform scope: requires ' +
        '`hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameTrailerParamsSchema },
    responses: {
        204: { description: 'The trailer was removed.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: trailerForbidden,
        404: problemResponse('No such game or trailer in the catalogue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const artSourceUnavailable = problemResponse('The art source is not configured or cannot answer.', 'art-source-unavailable');
export const listGameArtCandidatesRoute = createRoute({
    method: 'get',
    path: '/v1/hq/catalogue/games/{id}/art-candidates',
    operationId: 'listGameArtCandidates',
    tags: ['hq'],
    summary: 'Hero or logo candidates for a catalogue game',
    description: 'Lists the art HQ may pick for the game, best first: SteamGridDB heroes/logos (static images only, no NSFW, humour ' +
        'or epilepsy-flagged art), then IGDB artworks and screenshots as hero candidates (IGDB has no logos). Nothing is ' +
        'written. A source that is not configured (`STEAMGRIDDB_API_KEY`, the Twitch credentials) or cannot answer is named ' +
        'in `unavailableSources` and the other source still answers; when **no** source can answer for that kind, `503 ' +
        'art-source-unavailable`. A game id nobody holds is `404`. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameArtCandidatesParamsSchema, query: ArtCandidatesQuerySchema },
    responses: {
        200: jsonResponse(ArtCandidateListSchema, 'The candidates, best first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No game in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        503: artSourceUnavailable,
        500: commonProblemResponses[500],
    },
});
export const setGameArtRoute = createRoute({
    method: 'put',
    path: '/v1/hq/catalogue/games/{id}/art/{kind}',
    operationId: 'setGameArt',
    tags: ['hq'],
    summary: 'Pick the hero or logo of a catalogue game',
    description: 'Re-resolves the picked candidate against its source (the server never downloads a URL a client sent), downloads ' +
        'the full-size image from the source’s CDN (a hero at most 8 MB, a logo 4 MB), and stores it in our public-media ' +
        'bucket at a content-addressed, immutable key (`catalogue/heroes/{gameId}/{sha256}.{ext}`, ' +
        '`catalogue/logos/{gameId}/{sha256}.{ext}`). The game then serves `heroUrl`/`logoUrl` from our media CDN, and the ' +
        'venue library carries it to stations, which cache it (ADR-0028). Audited: one platform-level row ' +
        '(`catalog.game.art.set`). A candidate the source no longer lists, or an image that is not a PNG/JPEG/WebP or is too ' +
        'large, is `422`. Source not configured or down: `503 art-source-unavailable`. Object storage not configured: `500`. ' +
        'Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameArtParamsSchema, ...json(PickGameArtSchema) },
    responses: {
        200: jsonResponse(GameSchema, 'The game with its new art.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No game in the catalogue has that id.', 'not-found'),
        422: problemResponse('The candidate is gone, or its image is not one we store.', 'unprocessable'),
        429: commonProblemResponses[429],
        503: artSourceUnavailable,
        500: commonProblemResponses[500],
    },
});
export const clearGameArtRoute = createRoute({
    method: 'delete',
    path: '/v1/hq/catalogue/games/{id}/art/{kind}',
    operationId: 'clearGameArt',
    tags: ['hq'],
    summary: 'Clear the hero or logo of a catalogue game',
    description: 'Removes the game’s hero or logo: the game serves `null` for it, and the `catalog.cache_art` job does **not** pick ' +
        'a new default — clearing is HQ’s deliberate choice; picking again restores one. Audited: one platform-level row ' +
        '(`catalog.game.art.clear`). Idempotent. A game id nobody holds is `404`. Platform scope: requires `hq:catalog` and ' +
        'a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: GameArtParamsSchema },
    responses: {
        200: jsonResponse(GameSchema, 'The game without that art.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No game in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Apps, and detection rules for any title
// ---------------------------------------------------------------------------------------------
const appNotFound = problemResponse('No app in the catalogue has that id (a game id is not an app).', 'not-found');
const appNameTaken = problemResponse('Another app in the catalogue already has that name.', 'conflict');
export const createCatalogueAppRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/apps',
    operationId: 'createCatalogueApp',
    tags: ['hq'],
    summary: 'Add an app to the catalogue',
    description: 'Creates an **app** — a non-game title such as a browser, voice chat or a game launcher — in the platform ' +
        'catalogue: no IGDB record (`igdbId: null`), a suggested kiosk category, launch recipes and detection rules, and — ' +
        'for an app that installs per Windows account — how a station installs it for players (`provisioning`) and where it ' +
        'keeps a player’s sign-in (`signOutPaths`; F-CAT-09, ADR-0045). ' +
        'Its icon is set afterwards (upload, or a SteamGridDB pick). Venues then add it to their library like any game. ' +
        'An app name is unique among apps (case-insensitive): a second is `409`. Audited: one platform-level row ' +
        '(`catalog.app.create`). Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { ...json(CreateCatalogueAppSchema) },
    responses: {
        201: jsonResponse(GameSchema, 'The app just added.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        409: appNameTaken,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateCatalogueAppRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/catalogue/apps/{id}',
    operationId: 'updateCatalogueApp',
    tags: ['hq'],
    summary: 'Edit an app in the catalogue',
    description: 'Changes only the fields sent — name, summary, suggested category, launch recipes (the set is replaced), ' +
        'detection rules, the provisioning recipe (`null` removes it) or the sign-out paths (the set is replaced; F-CAT-09, ' +
        'ADR-0045). Venue library entries that offer the app carry the change to their stations on the next ' +
        'library pull. An id that is not an app is `404`; a name another app has is `409`. Audited with the before and ' +
        'after (`catalog.app.update`). Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: CatalogueAppParamsSchema, ...json(UpdateCatalogueAppSchema) },
    responses: {
        200: jsonResponse(GameSchema, 'The app as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: appNotFound,
        409: appNameTaken,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const seedCommonCatalogueAppsRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/apps/common',
    operationId: 'seedCommonCatalogueApps',
    tags: ['hq'],
    summary: 'Add the common apps to the catalogue',
    description: 'Creates the apps most venues offer — Google Chrome, Microsoft Edge, Mozilla Firefox, Discord, Spotify, OBS ' +
        'Studio, VLC, WhatsApp, Steam, Epic Games Launcher, Riot Client, Battle.net, EA app and Ubisoft Connect — each ' +
        'with its launch recipe, suggested category and detection rules — and, for the apps that install per Windows ' +
        'account (Discord, Spotify, WhatsApp), how a station installs them for players and where they keep a player’s ' +
        'sign-in (F-CAT-09, ADR-0045) — **only where the catalogue does not already hold an app of that name**: idempotent, ' +
        'so pressing it twice adds nothing the second time and never overwrites HQ’s edits. An app already held gets the ' +
        'provisioning recipe and sign-out paths only where it has none (audited `catalog.app.update`). Icons are left to HQ ' +
        '(or the `catalog.cache_art` job defaults one from SteamGridDB). Each app created is audited (`catalog.app.create`). ' +
        'Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    responses: {
        200: jsonResponse(SeedCommonAppsResultSchema, 'Every common app, and how many this call created.'),
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createAppIconUploadRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/apps/{id}/icon',
    operationId: 'createAppIconUpload',
    tags: ['hq'],
    summary: 'Reserve an icon upload for an app and get a signed upload URL',
    description: 'Returns a short-lived signed URL to PUT a square PNG or WebP icon (at most 1 MB) to our object storage — the ' +
        'bytes never pass through the api, as a trailer or a venue photo. The signature covers the declared ' +
        '`Content-Type` and `Content-Length`. Confirm with `POST …/icon/{uploadId}/complete`. An id that is not an app ' +
        'is `404`; object storage not configured answers `500`. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: CatalogueAppParamsSchema, ...json(CreateAppIconUploadSchema) },
    responses: {
        201: { description: 'Where to upload the icon.', content: { [JSON_CONTENT_TYPE]: { schema: AppIconUploadSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: appNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const completeAppIconUploadRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/apps/{id}/icon/{uploadId}/complete',
    operationId: 'completeAppIconUpload',
    tags: ['hq'],
    summary: 'Confirm an app icon upload',
    description: 'Reads the uploaded object back, checks its first bytes really are a PNG or WebP within 1 MB, and copies it to a ' +
        'content-addressed, immutable key (`catalogue/apps/{id}/{sha256}.{ext}`); the staged upload is removed either ' +
        'way. The app then serves `iconUrl` (and, as an app, `coverUrl`) from our media CDN, and the venue library ' +
        'carries it to stations. Audited: one platform-level row (`catalog.game.art.set`, kind `icon`). A missing or ' +
        'non-image upload is `422`. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: AppIconUploadParamsSchema },
    responses: {
        200: jsonResponse(GameSchema, 'The app with its new icon.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: appNotFound,
        422: problemResponse('The upload is missing, too large, or not a PNG or WebP.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const setCatalogueTitleDetectionRoute = createRoute({
    method: 'put',
    path: '/v1/hq/catalogue/games/{id}/detection',
    operationId: 'setCatalogueTitleDetection',
    tags: ['hq'],
    summary: "Set a catalogue title's detection rules",
    description: 'Replaces how a station recognises the title’s process (spec/10 §7.3) — process names (stored lower-case without ' +
        '`.exe`, deduplicated), an optional path pattern, an optional regex. For a game or an app. The venue library ' +
        'carries them to stations in the signed bundle. Audited (`catalog.game.detection.set`). A title id nobody holds ' +
        'is `404`. Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: TitleDetectionParamsSchema, ...json(TitleDetectionInputSchema) },
    responses: {
        200: jsonResponse(GameSchema, 'The title with its new detection rules.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No title in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createCatalogueGameRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/games/manual',
    operationId: 'createCatalogueGame',
    tags: ['hq'],
    summary: 'Create a catalogue game by hand',
    description: 'Creates a **game** with no metadata provider record (`igdbId: null`) — for when IGDB is down or does not know the ' +
        'title (spec/14 §6: a provider outage must not block creating a title). Name, summary, genres, publisher, age ' +
        'rating, tags and the `requiresElevation` flag; art is picked afterwards on Media. No refresh ever touches it. ' +
        'Needs no provider. Audited (`catalog.game.create`). Platform scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { ...json(CreateCatalogueGameSchema) },
    responses: {
        201: jsonResponse(GameSchema, 'The game just created.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateCatalogueGameRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/catalogue/games/{id}',
    operationId: 'updateCatalogueGame',
    tags: ['hq'],
    summary: 'Edit a catalogue game',
    description: 'Changes only the fields sent — name, summary, genres, franchise, release year, publisher, age rating, tags — and ' +
        'records each as **hand-edited** (`editedFields`), so the weekly refresh, a re-import and the on-demand refresh ' +
        'leave it exactly as HQ wrote it; `providerFields` hands fields back to the provider. `requiresElevation` sets the ' +
        'flag (spec/11 §3.5). Venues never edit a catalogue title (their own library entry only). An id that is not a game ' +
        'is `404`. Audited with the before and after (`catalog.game.update`). Platform scope: requires `hq:catalog` and a ' +
        'second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: CatalogueGameParamsSchema, ...json(UpdateCatalogueGameSchema) },
    responses: {
        200: jsonResponse(GameSchema, 'The game as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No game in the catalogue has that id (an app is edited on its own route).', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getCatalogueGameUsageRoute = createRoute({
    method: 'get',
    path: '/v1/hq/catalogue/games/{id}/usage',
    operationId: 'getCatalogueGameUsage',
    tags: ['hq'],
    summary: 'How many venues offer a catalogue title',
    description: 'The number of venue libraries (and their organisations) that offer the title — what archiving it warns about. ' +
        'Counts only, no organisation’s rows. Audited as one platform-level read (`catalog.game.usage.read`). Platform ' +
        'scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: CatalogueGameParamsSchema },
    responses: {
        200: jsonResponse(CatalogueGameUsageSchema, 'Where the title is offered.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No title in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const archiveCatalogueGameRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/games/{id}/archive',
    operationId: 'archiveCatalogueGame',
    tags: ['hq'],
    summary: 'Archive a catalogue title',
    description: 'Sets `archivedAt`: the title is hidden from the venues’ "add to library" browse, and the library entries that ' +
        'already offer it **keep working** (their stations keep launching and detecting it). Never a hard delete. ' +
        'Idempotent. Audited (`catalog.game.archive`) with how many venues offered it. Platform scope: requires ' +
        '`hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: CatalogueGameParamsSchema },
    responses: {
        200: jsonResponse(GameSchema, 'The archived title.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No title in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const unarchiveCatalogueGameRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/games/{id}/unarchive',
    operationId: 'unarchiveCatalogueGame',
    tags: ['hq'],
    summary: 'Bring an archived catalogue title back',
    description: 'Clears `archivedAt`, so venues can add the title again. Idempotent. Audited (`catalog.game.unarchive`). Platform ' +
        'scope: requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { params: CatalogueGameParamsSchema },
    responses: {
        200: jsonResponse(GameSchema, 'The title, no longer archived.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No title in the catalogue has that id.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const testCatalogueDetectionRoute = createRoute({
    method: 'post',
    path: '/v1/hq/catalogue/detection/test',
    operationId: 'testCatalogueDetection',
    tags: ['hq'],
    summary: 'Test a process list against the catalogue’s detection rules',
    description: 'Paste a process list — names or full image paths, one per line (a `tasklist /fo csv` dump works) — and see which ' +
        'catalogue titles each process would match on a station, and by which rule: the title’s own rules (process name ' +
        'with `.exe` stripped on both sides and case ignored, path pattern, regex — every rule set must hold), its exe ' +
        'recipe’s file name when it has no rules, or the curated defaults for a well-known title (spec/10 §7.3). Path ' +
        'variables in a pattern match anything here, since only a PC can resolve them. Nothing is written. Platform scope: ' +
        'requires `hq:catalog` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_CATALOG,
    request: { ...json(DetectionTestRequestSchema) },
    responses: {
        200: jsonResponse(DetectionTestResultSchema, 'Each process and the titles it matched.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const HQ_CATALOGUE_ROUTES = [
    searchCatalogueRoute,
    mirrorCatalogueGameRoute,
    refreshCatalogueGameRoute,
    setCatalogueGameLaunchRecipesRoute,
    listCatalogueGamesRoute,
    createGameTrailerRoute,
    listGameTrailersRoute,
    completeGameTrailerRoute,
    updateGameTrailerRoute,
    deleteGameTrailerRoute,
    listGameArtCandidatesRoute,
    setGameArtRoute,
    clearGameArtRoute,
    createCatalogueAppRoute,
    updateCatalogueAppRoute,
    seedCommonCatalogueAppsRoute,
    createAppIconUploadRoute,
    completeAppIconUploadRoute,
    setCatalogueTitleDetectionRoute,
    createCatalogueGameRoute,
    updateCatalogueGameRoute,
    getCatalogueGameUsageRoute,
    archiveCatalogueGameRoute,
    unarchiveCatalogueGameRoute,
    testCatalogueDetectionRoute,
];
//# sourceMappingURL=hq-catalogue.js.map